import { useProfileQuery } from '@services/hooks/profile/useProfileQuery';
import { QueryObserverResult } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useSiderToggleMenu } from '@store/useSiderToggleMenu';
import { DropdownQuery } from '@services/hooks/dropdowns/DropdownQuery';

interface IRefetchAllData {
	refetchAllData?: (refetchName?: string | string[]) => Promise<void>;
}

type RefetchFunction = () => Promise<QueryObserverResult>;

export const useRefetchAllData = (): IRefetchAllData => {
	const { refetch: refetchProfile } = useProfileQuery();
	const { refetch: refetchDropdowns } = DropdownQuery();

	const refetchMap: Record<string, RefetchFunction | undefined> = {
		Profile: refetchProfile,
		Dropdowns: refetchDropdowns,
	};

	const nameWiseRefetchData = async (refetchNames: string | string[]): Promise<void> => {
		const names = Array.isArray(refetchNames) ? refetchNames : [refetchNames];

		const refetchFunctions = names.map((name) => refetchMap[name]).filter((fn): fn is RefetchFunction => !!fn);

		const invalidNames = names.filter((name) => !refetchMap[name]);
		if (invalidNames.length > 0) {
			// eslint-disable-next-line no-console
			console.warn(`No refetch functions found for names: ${invalidNames.join(', ')}`);
		}

		if (refetchFunctions.length > 0) {
			await Promise.all(refetchFunctions.map((fn) => fn()));
		}
	};

	const refetchAllData = async (refetchName: string | string[] = 'all'): Promise<void> => {
		if (refetchName === 'all') {
			await Promise.all([refetchProfile?.()]);
		} else {
			await nameWiseRefetchData(refetchName);
		}
	};

	return { refetchAllData };
};

export const useRefetchList = (refetchName?: string | string[]): void => {
	const { setRefetchName } = useSiderToggleMenu();

	const memoizedRefetchName = useMemo(() => {
		if (!refetchName) return 'all';
		return Array.isArray(refetchName) ? refetchName.join('|') : refetchName;
	}, [refetchName]);

	useEffect(() => {
		setRefetchName(memoizedRefetchName.split('|'));

		return (): void => {
			setRefetchName('all');
		};
	}, [setRefetchName, memoizedRefetchName]);
};

export const usePageRefetch = (callback?: () => unknown): void => {
	const { setPageRefetch } = useSiderToggleMenu();

	useEffect(() => {
		// Register page-specific refetch for header button with current route
		if (callback) {
			setPageRefetch({
				pathname: window.location.pathname,
				fn: callback,
			});
		} else {
			setPageRefetch(null);
		}

		// Cleanup on unmount or when callback changes
		return (): void => {
			setPageRefetch(null);
		};
	}, [callback, setPageRefetch]);
};

export const useHeaderRefetch = (...refetchFns: Array<(() => unknown) | undefined>): void => {
	const combinedRefetch = useMemo(() => {
		const validFns = refetchFns.filter(Boolean) as Array<() => unknown>;
		if (validFns.length === 0) return undefined;

		return (): void => {
			validFns.forEach((fn) => fn());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...refetchFns]);

	usePageRefetch(combinedRefetch);
};
